import { useEffect, useMemo, useState } from "react";
import { matchesPhoneFilter, type AdminLogEntry, type AdminLogLevel } from "@/server/admin/logEntries";
import { LogDetailPanel } from "./LogDetailPanel";
import { formatLogTime, levelColor } from "../utils/logDisplay";

const LOGS_PAGE_SIZE = 50;

type LogsToolbarProps = {
  search: string;
  onSearchChange: (search: string) => void;
  phone: string;
  onPhoneChange: (phone: string) => void;
  level: AdminLogLevel | "ALL";
  onLevelChange: (level: AdminLogLevel | "ALL") => void;
  connected: boolean;
  singleLine: boolean;
  onSingleLineChange: (singleLine: boolean) => void;
  logCount: number;
  onRefresh: () => void;
};

const LogsToolbar = ({
  search,
  onSearchChange,
  phone,
  onPhoneChange,
  level,
  onLevelChange,
  connected,
  singleLine,
  onSingleLineChange,
  logCount,
  onRefresh,
}: LogsToolbarProps) => (
  <div className="admin-logs-toolbar">
    <input
      type="search"
      className="admin-control admin-logs-search"
      placeholder="Search logs…"
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
    />
    <input
      type="search"
      className="admin-control admin-logs-phone"
      placeholder="Phone (+1 555… or last digits)"
      value={phone}
      onChange={(event) => onPhoneChange(event.target.value)}
    />
    <div className="admin-logs-toolbar-row">
      <select
        className="admin-control"
        value={level}
        onChange={(event) => onLevelChange(event.target.value as AdminLogLevel | "ALL")}
      >
        <option value="ALL">All levels</option>
        <option value="DEBUG">DEBUG</option>
        <option value="INFO">INFO</option>
        <option value="WARN">WARN</option>
        <option value="ERROR">ERROR</option>
      </select>
      <span
        className="admin-live-status"
        role="status"
        aria-live="polite"
        aria-label={connected ? "Live stream connected" : "Reconnecting to live stream"}
      >
        <span
          className={`admin-live-dot ${connected ? "connected" : "reconnecting"}`}
          aria-hidden="true"
        />
        {connected ? "Live" : "Reconnecting…"}
      </span>
      <button type="button" className="admin-control admin-refresh-btn-inline" onClick={onRefresh}>
        Refresh
      </button>
      <label className="admin-live-toggle admin-logs-single-line">
        <input
          type="checkbox"
          checked={singleLine}
          onChange={(event) => onSingleLineChange(event.target.checked)}
        />
        Single line
      </label>
      <span className="admin-logs-count">{logCount} entries</span>
    </div>
  </div>
);

type LogsPaginationProps = {
  page: number;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  onPageChange: (page: number) => void;
};

const LogsPagination = ({
  page,
  pageCount,
  rangeStart,
  rangeEnd,
  total,
  onPageChange,
}: LogsPaginationProps) => {
  if (total === 0) return null;

  return (
    <div className="admin-logs-pagination" role="navigation" aria-label="Log pages">
      <span className="admin-logs-pagination-range">
        {rangeStart}–{rangeEnd} of {total}
      </span>
      <div className="admin-logs-pagination-controls">
        <button
          type="button"
          className="admin-control admin-logs-page-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        <span className="admin-logs-pagination-page">
          Page {page} of {pageCount}
        </span>
        <button
          type="button"
          className="admin-control admin-logs-page-btn"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

type LogsSectionProps = {
  logs: AdminLogEntry[];
  level: AdminLogLevel | "ALL";
  onLevelChange: (level: AdminLogLevel | "ALL") => void;
  search: string;
  onSearchChange: (search: string) => void;
  phone: string;
  onPhoneChange: (phone: string) => void;
  connected: boolean;
  onRefresh: () => void;
};

export const LogsSection = ({
  logs,
  level,
  onLevelChange,
  search,
  onSearchChange,
  phone,
  onPhoneChange,
  connected,
  onRefresh,
}: LogsSectionProps) => {
  const [singleLine, setSingleLine] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return logs.filter((entry) => {
      if (level !== "ALL" && entry.level !== level) {
        return false;
      }

      if (!matchesPhoneFilter(entry.phone, phone)) {
        return false;
      }

      if (!query) return true;

      const queryDigits = query.replace(/\D/g, "");

      return entry.message.toLowerCase().includes(query)
        || entry.runId?.toLowerCase().includes(query)
        || (queryDigits.length > 0 && (entry.phone?.replace(/\D/g, "").includes(queryDigits) ?? false));
    });
  }, [logs, level, phone, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / LOGS_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * LOGS_PAGE_SIZE;
  const pageEntries = filtered.slice(pageStart, pageStart + LOGS_PAGE_SIZE);
  const rangeStart = filtered.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = Math.min(pageStart + LOGS_PAGE_SIZE, filtered.length);

  useEffect(() => {
    setPage(1);
  }, [level, phone, search]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const selectedIndex = selectedId === null
    ? -1
    : pageEntries.findIndex((entry) => entry.id === selectedId);

  const selectedEntry = selectedIndex >= 0 ? pageEntries[selectedIndex] : null;

  useEffect(() => {
    if (selectedId !== null && selectedIndex === -1) {
      setSelectedId(null);
    }
  }, [selectedId, selectedIndex]);

  useEffect(() => {
    if (!selectedEntry) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedId(null);
        return;
      }

      if (event.key === "ArrowUp" && selectedIndex > 0) {
        event.preventDefault();
        setSelectedId(pageEntries[selectedIndex - 1]!.id);
        return;
      }

      if (event.key === "ArrowDown" && selectedIndex < pageEntries.length - 1) {
        event.preventDefault();
        setSelectedId(pageEntries[selectedIndex + 1]!.id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedEntry, selectedIndex, pageEntries]);

  const handleRowClick = (entry: AdminLogEntry) => {
    setSelectedId((current) => (current === entry.id ? null : entry.id));
  };

  return (
    <section className="admin-section admin-logs-page">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Logs</h2>
      </div>
      <LogsToolbar
        search={search}
        onSearchChange={onSearchChange}
        phone={phone}
        onPhoneChange={onPhoneChange}
        level={level}
        onLevelChange={onLevelChange}
        connected={connected}
        singleLine={singleLine}
        onSingleLineChange={setSingleLine}
        logCount={filtered.length}
        onRefresh={onRefresh}
      />
      <div className={`admin-logs-body${selectedEntry ? " has-detail" : ""}`}>
        <div className="admin-log-table-wrap-full">
          <div className="admin-log-table-wrap">
            <table className="admin-log-table">
              <thead>
                <tr>
                  <th style={{ width: "170px" }}>Time</th>
                  <th style={{ width: "72px" }}>Level</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {pageEntries.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="admin-empty py-8 text-center">
                      No log entries yet.
                    </td>
                  </tr>
                ) : (
                  pageEntries.map((entry) => {
                    const isSelected = entry.id === selectedId;
                    const rowClass = [
                      "admin-log-row-clickable",
                      entry.level === "ERROR" ? "error-row" : "",
                      isSelected ? "admin-log-row-selected" : "",
                    ].filter(Boolean).join(" ");

                    return (
                      <tr
                        key={entry.id}
                        className={rowClass || undefined}
                        onClick={() => handleRowClick(entry)}
                        aria-selected={isSelected}
                      >
                        <td className="admin-log-time">{formatLogTime(entry.ts)}</td>
                        <td style={{ color: levelColor[entry.level], fontWeight: 500 }}>
                          {entry.level}
                        </td>
                        <td
                          className={`admin-log-message${singleLine ? " admin-log-single-line" : ""}`}
                        >
                          {entry.message}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <LogsPagination
            page={safePage}
            pageCount={pageCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={filtered.length}
            onPageChange={setPage}
          />
        </div>

        {selectedEntry ? (
          <LogDetailPanel
            entry={selectedEntry}
            hasPrev={selectedIndex > 0}
            hasNext={selectedIndex < pageEntries.length - 1}
            onPrev={() => setSelectedId(pageEntries[selectedIndex - 1]!.id)}
            onNext={() => setSelectedId(pageEntries[selectedIndex + 1]!.id)}
            onClose={() => setSelectedId(null)}
          />
        ) : null}
      </div>
    </section>
  );
};
